import type { Amount as AmountEntry } from '../components/amounts/amounts'
import type { Column, SortDirection } from '../simple-table'
import type { Claim } from './claim'

import * as Sentry from '@sentry/react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { MdClose, MdCropFree, MdZoomInMap } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'react-use'

import http from '../../../lib/http'
import { FriendlyDateTime } from '../../shared/Date/friendly-date-time'
import { Amounts } from '../components/amounts/amounts'
import { Filters } from '../components/filters'
import { useDebouncedFilters } from '../hooks/use-debounced-filters'
import * as Table from '../simple-table'

import styles from './index.module.css'
import { claimsSchema } from './schema'

let priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

function formatPrice (price?: number | null): string | null {
  if (typeof price !== 'number') {
    return null
  }

  return priceFormatter.format(price)
}

function Amount ({ value }: { value?: number | null }): JSX.Element {
  return (
    <div className={styles.amount}>
      {formatPrice(value)}
    </div>
  )
}

let amountColumns: Array<Column<Claim>> = [
  {
    name: 'charge_amount',
    header: 'Charge Amt',
    gridArea: '0.8fr',
    getValue (item) {
      return (
        <Amount value={item.charge_amount} />
      )
    }
  },
  {
    name: 'ppo_discount',
    header: 'PPO Discount',
    gridArea: '0.8fr',
    getValue (item) {
      return (
        <Amount value={item.ppo_discount} />
      )
    }
  },
  {
    name: 'ineligible_amount',
    header: 'Ineligible Amt',
    gridArea: '0.8fr',
    getValue (item) {
      return (
        <Amount value={item.ineligible_amount} />
      )
    }
  },
  {
    name: 'deductible',
    header: 'Deductible',
    gridArea: '0.8fr',
    getValue (item) {
      return (
        <Amount value={item.deductible} />
      )
    }
  },
  {
    name: 'out_of_pocket',
    header: 'Out of Pocket',
    gridArea: '0.8fr',
    getValue (item) {
      return (
        <Amount value={item.ppo_discount} />
      )
    }
  },
  {
    name: 'coinsurance',
    header: 'Co-Insurance',
    gridArea: '0.8fr',
    getValue (item) {
      return (
        <Amount value={item.coinsurance} />
      )
    }
  },
  {
    name: 'total_paid',
    header: 'Total Paid',
    gridArea: '0.8fr',
    getValue (item) {
      return (
        <Amount value={item.total_paid} />
      )
    }
  },
  {
    name: 'billing_date',
    header: 'Billing Date',
    getValue (item) {
      if (typeof item.billing_date !== 'string') {
        return null
      }
      return Table.formatDate(item.billing_date)
    }
  },
  {
    name: 'paid_date',
    header: 'Paid Date',
    getValue (item) {
      if (typeof item.paid_date !== 'string') {
        return null
      }
      return Table.formatDate(item.paid_date)
    }
  }
]

type ClaimsQueryKey = [
  string,
  string,
  SearchParams
]

type AmountsQueryKey = [
  string,
  string
]

interface ClaimsResponse {
  data: Claim[]
  filters: Record<string, string[]>
  total_filtered: number
  total_unfiltered: number
  updated_at: string
}

interface AmountsResponse {
  severity: number
  amounts: AmountEntry[]
  updated_at: string
}

interface SearchParams extends Record<string, unknown>{
  query: string

  filters: {
    code: string[]
    procedure_code: string[]
    coverage_category: string[]
    severity: string[]
    provider_name: string[]
    place_of_service: string[]
    year: string[]
    claim_status: string[]
  }

  sort: {
    field: keyof Claim
    direction: SortDirection
  }

  pagination: {
    page: number
    pageSize: number
  }
}

let defaultSearchParams: SearchParams = {
  query: '',

  filters: {
    code: [],
    procedure_code: [],
    coverage_category: [],
    severity: [],
    provider_name: [],
    place_of_service: [],
    year: [],
    claim_status: []
  },

  sort: {
    field: 'service_date',
    direction: 'asc'
  },

  pagination: {
    page: 0,
    pageSize: 10
  }
}

let columns: Array<Column<Claim>> = [
  {
    name: 'service_date',
    header: 'Service Date',
    getValue (item) {
      if (typeof item.service_date !== 'string') {
        return null
      }
      return Table.formatDate(item.service_date)
    },
    isSortable: true,
    isMergeable: true
  },
  {
    name: 'claim_number',
    header: 'Claim #',
    gridArea: '1.4fr',
    isMergeable: true
  },
  {
    name: 'description',
    header: 'Code & Descriptor',
    getValue (item) {
      return (
        <div className={styles.description}>
          {item.description?.map(({ code, descriptor }, i) => (
            code ?? descriptor
              ? (
                <div key={code ?? i}>
                  <strong>{code}</strong>
                  {' '}
                  {descriptor}
                </div>)
              : null
          ))}
        </div>
      )
    },
    gridArea: 'minmax(18rem, 2fr)'
  },
  {
    name: 'provider_name',
    header: 'Provider Name',
    gridArea: 'minmax(12rem, 2fr)'
  },
  {
    name: 'procedure',
    header: 'Procedure',
    gridArea: 'minmax(16rem, 2fr)',
    getValue ({ procedure }) {
      if (!procedure) {
        return null
      }

      let { code, descriptor } = procedure

      return (
        <span>
          <strong>{code}</strong>
          {' '}
          {descriptor}
        </span>
      )
    }
  },
  ...amountColumns,
  {
    name: 'place_of_service',
    header: 'Place of Service'
  },
  {
    name: 'claim_status',
    header: 'Claim Status'
  }
]

const collapsedColumns: Array<Column<Claim>> = [
  {
    name: 'service_date',
    header: 'Service Date',
    getValue (item) {
      if (typeof item.service_date !== 'string') {
        return null
      }
      return Table.formatDate(item.service_date)
    },
    isSortable: true,
    isMergeable: true,
    gridArea: '0.75fr'
  },
  {
    name: 'claim_number',
    header: 'Claim #',
    gridArea: '0.75fr',
    isMergeable: true
  },
  {
    name: 'description',
    header: 'Code & Descriptor',
    getValue (item) {
      return (
        <div className={styles.description}>
          {item.description?.map(({ code, descriptor }, i) => (
            code ?? descriptor
              ? (
                <div key={code ?? i}>
                  <strong>{code}</strong>
                  {' '}
                  {descriptor}
                </div>)
              : null
          ))}
        </div>
      )
    },
    gridArea: 'minmax(15rem, 1fr)'
  },
  {
    name: 'provider_name',
    header: 'Provider Name'
  },
  {
    name: 'procedure',
    header: 'Procedure',
    gridArea: '1.1fr',
    getValue ({ procedure }) {
      if (!procedure) {
        return null
      }

      let { code, descriptor } = procedure

      return (
        <span>
          <strong>{code}</strong>
          {' '}
          {descriptor}
        </span>
      )
    }
  }, {
    name: 'place_of_service',
    header: 'Place of Service',
    gridArea: '0.9fr'
  },
  {
    name: 'claim_status',
    header: 'Claim Status',
    gridArea: '0.75fr'
  }
]

function isDefaultSearchParams (params: SearchParams): boolean {
  return JSON.stringify(params) === JSON.stringify(defaultSearchParams)
}

// let filterKeys = Object.keys(defaultSearchParams.filters) as Array<keyof SearchParams['filters']>

let filterNameMapper: Record<string, string> = {
  year: 'service_date',
  code: 'description',
  procedure_code: 'procedure'
}

let reverseFilterNameMapper: Record<string, string> = Object
  .keys(filterNameMapper)
  .reduce((mapper, key) => ({
    ...mapper,
    [filterNameMapper[key] as string]: key
  }), {})

export function DefaultClaimsTable (): JSX.Element {
  let [ amountsCollapsed, setAmountsCollapsed ] = useState(false)
  let [ urlSearchParams, setUrlSearchParams ] = useSearchParams()
  let queryClient = useQueryClient()

  const initialSearchParams = useMemo(() => ({
    ...defaultSearchParams,
    query: urlSearchParams.get('query') ?? defaultSearchParams.query,
    filters: {
      ...defaultSearchParams.filters,
      ...Object.keys(defaultSearchParams.filters)
        .reduce((acc, key) => ({
          ...acc,
          [key]: [
            ...defaultSearchParams.filters[key as keyof SearchParams['filters']].filter(value => !urlSearchParams.getAll(reverseFilterNameMapper[key] ?? key).includes(value)),
            ...urlSearchParams.getAll(reverseFilterNameMapper[key] ?? key)
          ]
        }), {})
    }
  }), [ urlSearchParams ])

  let [
    debouncedSearchParams,
    searchParams,
    setSearchParams,
    setDebouncedSearchParams
  ] = useDebouncedFilters<SearchParams>(initialSearchParams)

  let [ forcedFilterKeys, setForcedFilterKeys ] = useState<string[]>(urlSearchParams.getAll('force'))
  let [ searchQuery, setSearchQuery ] = useState(searchParams.query)

  useDebounce(() => {
    setDebouncedSearchParams(params => ({
      ...params,
      query: searchQuery,
      pagination: {
        ...params.pagination,
        page: 0
      }
    }))
  }, 500, [ searchQuery ])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let resource = useSelector<any, string>(state => state.globalVars?.params?.id)

  let claimsQuery = useQuery<ClaimsResponse, unknown, ClaimsResponse, ClaimsQueryKey>(
    {
      queryKey: [
        'claims-history/all',
        resource,
        debouncedSearchParams
      ],
      queryFn: async function getClaims ({ queryKey }): Promise<ClaimsResponse> {
        let [ , resource, searchParams ] = queryKey
        let { query, filters, sort, pagination } = searchParams
        let normalizedQuery = query.trim().toLowerCase()

        let urlSearchParams = new URLSearchParams([
          [ 'on', sort.field ],
          [ 'dir', sort.direction ],
          [ 'page', pagination.page.toString(10) ],
          [ 'size', pagination.pageSize.toString(10) ],
          ...filters.severity.map(severity => [ 'severity', severity.toString() ]),
          ...filters.coverage_category.map(category => [ 'coverage_category', category ]),
          ...filters.provider_name.map(provider => [ 'provider_name', provider ]),
          ...filters.place_of_service.map(place => [ 'place_of_service', place ]),
          ...filters.year.map(year => [ 'year', year.toString() ]),
          ...filters.claim_status.map(status => [ 'claim_status', status ]),
          ...filters.code.map(code => [ 'code', code ]),
          ...filters.procedure_code.map(code => [ 'procedure_code', code ])
        ])

        if (normalizedQuery.length > 0) {
          urlSearchParams.set('search', normalizedQuery)
        }

        let response: ClaimsResponse = await http.getRequest(`/patients/${resource}/claims?${urlSearchParams.toString()}`)
        let data = claimsSchema.parse(response.data)

        return {
          ...response,
          data
        }
      },
      enabled: typeof resource === 'string',
      retry: false,
      staleTime: 1000 * 60 * 5,
      placeholderData: keepPreviousData,
      refetchOnReconnect: true,
      refetchOnWindowFocus: false
    })

  let amountsQuery = useQuery<AmountsResponse, unknown, AmountsResponse, AmountsQueryKey>({
    queryKey: [
      'claims-history/amounts',
      resource
    ],
    queryFn: async function getAmounts ({ queryKey }): Promise<AmountsResponse> {
      let [ , resource ] = queryKey
      let response: AmountsResponse = await http.getRequest(`/patients/${resource}/claims/totals`)

      return {
        ...response,
        amounts: response.amounts
          .slice()
          .sort((a, b) => parseInt(b.year, 10) - parseInt(a.year, 10))
      }
    },
    enabled: typeof resource === 'string',
    retry: false,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false
  })

  let handlePageChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: value - 1
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handlePageSizeChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0,
        pageSize: value
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handleSortChange = useCallback((field: keyof Claim, direction: SortDirection): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      sort: {
        ...params.sort,
        field,
        direction
      }
    }))
  }, [ setSearchParams ])

  let handleFilterChange = useCallback((name: string, value: Array<string | number | boolean>): void => {
    let key = reverseFilterNameMapper[name] ?? name

    setUrlSearchParams(urlSearchParams => {
      urlSearchParams.delete(key)

      for (let v of value) {
        urlSearchParams.append(key, v.toString(10))
      }

      return urlSearchParams
    })
  }, [ setUrlSearchParams ])

  let handleFiltersClear = useCallback((): void => {
    setUrlSearchParams({})
    setSearchQuery('')
  }, [ setUrlSearchParams ])

  let filters = useMemo(() => {
    let keys = [
      'severity',
      'provider_name',
      'place_of_service',
      'year',
      'claim_status',
      'code',
      'procedure_code'
    ]

    return keys.map(key => {
      let name = filterNameMapper[key] ?? key

      let options = (claimsQuery.data?.filters[key] ?? [])
        .map((value: string | number) => ({ value: value.toString(10) }))

      return {
        name,
        // @ts-expect-error // TODO: fix this
        value: searchParams.filters[key],
        options,
        forced: forcedFilterKeys.includes(key)
      }
    })
  }, [ claimsQuery.data?.filters, searchParams.filters, forcedFilterKeys ])

  let globalFilters = useMemo(() => {
    let keys = [ 'coverage_category' ]

    return keys.reduce((acc, key) => {
      let values = claimsQuery.data?.filters[key] ?? []
      // @ts-expect-error // TODO: fix this
      let selected = searchParams.filters[key]?.[0] ?? null

      return {
        ...acc,
        [key]: {
          selected,
          values
        }
      }
    }, {})
  }, [ claimsQuery.data?.filters, searchParams.filters ])

  let handleGlobalFiltersChange = useCallback((name: string, value: string | number | null): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      filters: {
        ...params.filters,
        [name]: value !== null
          ? [ value ]
          : []
      }
    }))
  }, [ setSearchParams ])

  let forcedFilters = filters.filter(filter => filter.forced)

  return (
    <>
      <Amounts
        amounts={amountsQuery.data?.amounts ?? []}
        queryStatus={amountsQuery.status}
        fetchStatus={amountsQuery.fetchStatus}
        severity={amountsQuery.data?.severity ?? null}
      />

      <div className={cx(styles.card)}>
        <Table.Table
          header={(
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
              <div style={{ display: 'flex', gap: '2rem', alignItems: 'baseline' }}>
                <h2>
                  Claims History | All
                </h2>

                {claimsQuery.status === 'success' && forcedFilters.length > 0 && (
                  <div className={styles.forcedFilters}>
                    {forcedFilters.map(filter => {
                      function closeFilter (value: string): void {
                        let newValue = filter.value?.filter((v: string) => v !== value) ?? []
                        handleFilterChange(filter.name, newValue)

                        if (!newValue.length) {
                          let key = reverseFilterNameMapper[filter.name] ?? filter.name
                          setForcedFilterKeys(keys => keys.filter(k => k !== key))

                          setUrlSearchParams(sp => {
                            let all = sp.getAll('force').filter(k => k !== key)
                            sp.delete('force')
                            all.forEach(k => sp.append('force', k))
                            return sp
                          })
                        }
                      }

                      switch (filter.name) {
                        case 'description':
                          return (
                            <Fragment key={filter.name}>
                              {filter.value.map((value: string) => {
                                let description = claimsQuery.data?.data.flatMap(claim => claim.description ?? []).find(({ code }) => code === value)?.descriptor

                                return (
                                  <ForcedFilter
                                    key={value}
                                    title={value}
                                    description={description ?? undefined}
                                    onClose={() => {
                                      closeFilter(value)
                                    }}
                                  />
                                )
                              })}
                            </Fragment>
                          )

                        case 'provider_name':
                          return (
                            <Fragment key={filter.name}>
                              {filter.value.map((value: string) => {
                                return (
                                  <ForcedFilter
                                    key={value}
                                    description={value}
                                    onClose={() => {
                                      closeFilter(value)
                                    }}
                                  />
                                )
                              })}
                            </Fragment>
                          )

                        case 'procedure':
                          return (
                            <Fragment key={filter.name}>
                              {filter.value.map((value: string) => {
                                let description = claimsQuery.data?.data.flatMap(claim => claim.procedure ?? []).find(({ code }) => code === value)?.descriptor

                                return (
                                  <ForcedFilter
                                    key={value}
                                    title={value}
                                    description={description ?? undefined}
                                    onClose={() => {
                                      closeFilter(value)
                                    }}
                                  />
                                )
                              })}
                            </Fragment>
                          )

                        default:
                          return null
                      }
                    })}
                  </div>
                )}
              </div>

              {claimsQuery.status === 'success' && (
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <Filters
                    search={searchQuery}
                    filters={globalFilters}
                    onSearchChange={value => {
                      setSearchQuery(value)
                      setUrlSearchParams(sp => {
                        if (!value.length) {
                          sp.delete('query')
                        }

                        if (value.length > 0) {
                          sp.set('query', value)
                        }

                        return sp
                      })
                    }}
                    onFilterChange={handleGlobalFiltersChange}
                  />

                  <button
                    type='button'
                    title={amountsCollapsed ? 'Expand amounts' : 'Collapse amount columns'}
                    className={styles.collapseColumnsButton}
                    onClick={() => {
                      setAmountsCollapsed(collapsed => !collapsed)
                    }}
                  >
                    {amountsCollapsed && (
                      <MdCropFree size='1.1rem' />
                    )}

                    {!amountsCollapsed && (
                      <MdZoomInMap size='1.1rem' />
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          columns={amountsCollapsed ? collapsedColumns : columns}
          data={claimsQuery.data?.data ?? []}
          sort={searchParams.sort}
          pagination={{
            ...searchParams.pagination,
            totalFiltered: claimsQuery.data?.total_filtered,
            totalUnfiltered: claimsQuery.data?.total_unfiltered,
            extra: claimsQuery.data?.updated_at
              ? (
                <span>
                  Claims information refreshed
                  {' '}
                  <FriendlyDateTime
                    datetime={claimsQuery.data.updated_at}
                    diff
                  />
                </span>
              )
              : undefined
          }}
          filters={filters}
          error={claimsQuery.error as Error}
          queryStatus={claimsQuery.status}
          fetchStatus={claimsQuery.fetchStatus}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onFilterChange={handleFilterChange}
          onFiltersClear={handleFiltersClear}
          onRefetch={() => {
            if (isDefaultSearchParams(searchParams)) {
              return claimsQuery.refetch()
            }

            queryClient.invalidateQueries({
              queryKey: [
                'claims-history/all',
                resource
              ]
            })
              .then(() => {
                setSearchParams(defaultSearchParams)
              })
              .catch(err => {
                console.error(err)
                Sentry.captureException(err)
              })
          }}
        />
      </div>
    </>
  )
}

interface ForcedFilterProps {
  title?: string
  description?: string
  onClose: () => void
}

function ForcedFilter ({ title, description, onClose }: ForcedFilterProps): JSX.Element {
  return (
    <div className={styles.forcedFilter}>
      {title && (
        <span className={styles.code}>
          {title}
        </span>
      )}

      {description && (
        <span className={styles.description}>
          {description}
        </span>
      )}

      <button
        type='button'
        className={styles.close}
        onClick={onClose}
      >
        <MdClose />
      </button>
    </div>
  )
}
